import { create } from 'zustand';
import {
  supabase,
  DBStockRequest,
  DBStockRequestLine,
  DBStockRequestEvent,
} from '../lib/supabase';
import { getActiveCollectionId } from './collectionStore';
import { useAuthStore, getActedByName } from './authStore';
import { useInventoryStore } from './inventoryStore';
import { useStockRequestSettingsStore } from './stockRequestSettingsStore';
import {
  hasPendingNegotiation,
  canAutoApproveAfterNegotiation,
  defaultManagerIds,
  pickAssignee,
  type AssignmentStep,
  type SubstituteStatus,
} from './stockRequestAssignment';
import { markOwnWrite, isOwnWrite } from '../lib/realtime/ownWrites';

export type { AssignmentStep };

// ── Domain types ──────────────────────────────────────────────────────────────

export type RequestStatus =
  | 'draft'
  | 'submitted'
  | 'approved'
  | 'partially_ordered'
  | 'ordered'
  | 'partially_received'
  | 'completed'
  | 'cancelled';

export type LineStatus =
  | 'draft'
  | 'submitted'
  | 'pending_resolution'
  | 'approved'
  | 'ordered'
  | 'partially_received'
  | 'received'
  | 'cancelled'
  | 'unavailable'
  | 'backordered'
  | 'disputed';

export interface StockRequestLine {
  id: string;
  requestId: string;
  lineType: 'catalog' | 'free_text';
  itemId: string | null;
  freeTextDescription: string | null;
  substituteItemId: string | null;
  substituteStatus: 'pending_requester' | 'pending_manager' | 'accepted' | 'declined' | null;
  parentLineId: string | null;
  disputeReason: string | null;
  requestedQty: number;
  approvedQty: number | null;
  orderedQty: number | null;
  orderedAt: string | null;
  receivedQty: number;
  condition: 'new' | 'used';
  status: LineStatus;
  approvalReason: string | null;
  resolutionNotes: string | null;
  poReference: string | null;
  supplier: string | null;
  expectedDate: string | null;
  sortOrder: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface StockRequest {
  id: string;
  collectionId: string;
  requestNumber: number;
  requesterId: string;
  warehouseId: string;
  storageAreaId: string | null;
  priority: 'low' | 'medium' | 'high';
  notes: string | null;
  siteId: string | null;
  jobId: string | null;
  status: RequestStatus;
  submittedAt: Date | null;
  approvedAt: Date | null;
  reviewedBy: string | null;
  assignedTo: string | null;
  assignmentStep: AssignmentStep | null;
  orderedAt: Date | null;
  firstReceivedAt: Date | null;
  completedAt: Date | null;
  createdAt: Date;
  updatedAt: Date;
  lines?: StockRequestLine[];
  events?: StockRequestEvent[];
}

export interface StockRequestEvent {
  id: string;
  requestId: string;
  lineId: string | null;
  userId: string;
  userDisplayName: string;
  eventType: string;
  description: string | null;
  payload: Record<string, unknown>;
  createdAt: Date;
}

export interface NewRequestLineInput {
  lineType: 'catalog' | 'free_text';
  itemId?: string | null;
  freeTextDescription?: string | null;
  requestedQty: number;
  condition: 'new' | 'used';
}

/** Open (non-completed) request that already includes a catalogue item. */
export interface OpenItemConflict {
  requestId: string;
  requestNumber: number;
  status: RequestStatus;
  lineStatus: LineStatus;
  requestedQty: number;
}

export interface LineApprovalInput {
  lineId: string;
  approvedQty: number;
  approvalReason?: string | null;
  status?: LineStatus;
  itemId?: string | null;
  substituteItemId?: string | null;
  resolutionNotes?: string | null;
  poReference?: string | null;
  expectedDate?: string | null;
}

/** Persists per-line review progress before final Approve / Send for review. */
export interface LineReviewDraftInput {
  lineId: string;
  disposition?: 'approve' | 'unavailable' | 'substitute' | null;
  approvedQty?: number;
  approvalReason?: string | null;
  unavailableReason?: string | null;
  substituteItemId?: string | null;
  matchItemId?: string | null;
  resolutionNotes?: string | null;
}

export interface LineOrderInput {
  lineId: string;
  poReference?: string | null;
  supplier?: string | null;
  expectedDate?: string | null;
  orderedQty?: number | null;
  orderedAt?: string | null;
}

export interface LineApproveInput {
  lineId: string;
  approvedQty: number;
}

export interface LineReceiveUnit {
  serial?: string;
  mac?: string;
}

export interface LineReceiveInput {
  lineId: string;
  qty: number;
  units: LineReceiveUnit[];
  skipSerialValidation?: boolean;
  skipReason?: string;
}

export interface ReceiveIssueInput {
  lineId: string;
  reason: string;
  qty?: number;
  itemId?: string | null;
  units?: LineReceiveUnit[];
  skipSerialValidation?: boolean;
  skipReason?: string;
}

// ── Mappers ───────────────────────────────────────────────────────────────────

const toLine = (row: DBStockRequestLine): StockRequestLine => ({
  id: row.id,
  requestId: row.request_id,
  lineType: row.line_type,
  itemId: row.item_id,
  freeTextDescription: row.free_text_description,
  substituteItemId: row.substitute_item_id,
  substituteStatus: row.substitute_status,
  parentLineId: row.parent_line_id,
  disputeReason: row.dispute_reason,
  requestedQty: row.requested_qty,
  approvedQty: row.approved_qty,
  orderedQty: row.ordered_qty ?? null,
  orderedAt: row.ordered_at ?? null,
  receivedQty: row.received_qty,
  condition: row.condition,
  status: row.status as LineStatus,
  approvalReason: row.approval_reason,
  resolutionNotes: row.resolution_notes,
  poReference: row.po_reference,
  supplier: row.supplier,
  expectedDate: row.expected_date,
  sortOrder: row.sort_order,
  createdAt: new Date(row.created_at),
  updatedAt: new Date(row.updated_at),
});

const toRequest = (row: DBStockRequest, lines?: StockRequestLine[], events?: StockRequestEvent[]): StockRequest => ({
  id: row.id,
  collectionId: row.collection_id,
  requestNumber: row.request_number,
  requesterId: row.requester_id,
  warehouseId: row.warehouse_id,
  storageAreaId: row.storage_area_id,
  priority: row.priority,
  notes: row.notes,
  siteId: row.site_id,
  jobId: row.job_id,
  status: row.status as RequestStatus,
  submittedAt: row.submitted_at ? new Date(row.submitted_at) : null,
  approvedAt: row.approved_at ? new Date(row.approved_at) : null,
  reviewedBy: row.reviewed_by,
  assignedTo: row.assigned_to,
  assignmentStep: (row.assignment_step as AssignmentStep | null) ?? null,
  orderedAt: row.ordered_at ? new Date(row.ordered_at) : null,
  firstReceivedAt: row.first_received_at ? new Date(row.first_received_at) : null,
  completedAt: row.completed_at ? new Date(row.completed_at) : null,
  createdAt: new Date(row.created_at),
  updatedAt: new Date(row.updated_at),
  lines,
  events,
});

const toEvent = (row: DBStockRequestEvent): StockRequestEvent => ({
  id: row.id,
  requestId: row.request_id,
  lineId: row.line_id,
  userId: row.user_id,
  userDisplayName: row.user_display_name,
  eventType: row.event_type,
  description: row.description,
  payload: row.payload ?? {},
  createdAt: new Date(row.created_at),
});

export function deriveRequestStatus(lines: StockRequestLine[]): RequestStatus {
  if (lines.length === 0) return 'draft';
  const terminal = new Set(['cancelled', 'unavailable', 'backordered']);
  const active = lines.filter(l => !terminal.has(l.status));
  if (active.length === 0) return 'cancelled';
  if (active.every(l => l.status === 'received')) return 'completed';
  if (active.some(l => l.receivedQty > 0)) return 'partially_received';
  if (active.every(l => ['ordered', 'partially_received', 'received', 'backordered'].includes(l.status))) return 'ordered';
  const hasOrdered = active.some(l => ['ordered', 'partially_received', 'received', 'backordered'].includes(l.status));
  const hasApproved = active.some(l => l.status === 'approved');
  if (hasOrdered && hasApproved) return 'partially_ordered';
  if (active.every(l => ['approved', 'ordered', 'partially_received', 'received', 'backordered'].includes(l.status))) return 'approved';
  if (active.some(l => l.status === 'submitted' || l.status === 'pending_resolution')) return 'submitted';
  return 'draft';
}


async function logEvent(
  requestId: string,
  eventType: string,
  description: string,
  lineId?: string | null,
  payload: Record<string, unknown> = {},
) {
  const user = useAuthStore.getState().user;
  if (!user) return;
  await supabase.from('stock_request_events').insert({
    request_id: requestId,
    line_id: lineId ?? null,
    user_id: user.id,
    user_display_name: getActedByName() ?? 'Unknown',
    event_type: eventType,
    description,
    payload,
  });
}

async function notifyJobStockReceipt(
  requestId: string,
  prevStatus: string,
  getState: () => { requests: StockRequest[]; selectedRequest: StockRequest | null },
) {
  const req =
    getState().selectedRequest?.id === requestId
      ? getState().selectedRequest
      : getState().requests.find(r => r.id === requestId);
  if (!req?.jobId) return;

  const actorId = useAuthStore.getState().user?.id;
  const { useSitesStore } = await import('./sitesStore');
  const { useUsersStore } = await import('./usersStore');
  const { useStockNotificationsStore } = await import('./stockNotificationsStore');

  const sites = useSitesStore.getState().sites;
  const site =
    (req.siteId ? sites.find(s => s.id === req.siteId) : null)
    ?? sites.find(s => s.jobs?.some(j => j.id === req.jobId))
    ?? null;
  const job = site?.jobs?.find(j => j.id === req.jobId);
  const jobTitle = job?.title ?? 'Job';
  const ref = formatRequestRef(req.requestNumber);
  const { received, total } = requestReceiveProgress(req);

  const recipientIds = new Set<string>();
  if (req.requesterId) recipientIds.add(req.requesterId);
  if (job?.assignedTo) {
    const assignee = useUsersStore.getState().users.find(u => u.displayName === job.assignedTo);
    if (assignee) recipientIds.add(assignee.id);
  }
  if (actorId) recipientIds.delete(actorId);
  if (recipientIds.size === 0) return;

  const fullyReceived = req.status === 'completed' && prevStatus !== 'completed';
  const type = fullyReceived ? 'job_stock_ready' : 'job_stock_partial';
  const message = fullyReceived
    ? `Stock now available for Job: ${jobTitle} — ${ref} fully received`
    : `Stock received for Job: ${jobTitle} — ${ref} (${received} of ${total} items)`;

  const siteId = req.siteId ?? site?.id ?? null;
  const notify = useStockNotificationsStore.getState().notify;
  for (const recipientId of Array.from(recipientIds)) {
    await notify(recipientId, requestId, type, message, null, req.jobId, siteId);
  }
}

function itemNameFromStore(itemId: string | null | undefined, fallback?: string | null): string {
  if (!itemId) return fallback?.trim() || 'Unknown item';
  return useInventoryStore.getState().items.find(i => i.id === itemId)?.name
    ?? fallback?.trim()
    ?? 'Unknown item';
}

function substituteActivityDesc(
  verb: string,
  originalItemId: string | null,
  substituteItemId: string | null,
  freeText?: string | null,
  qty?: number,
): { description: string; payload: Record<string, unknown> } {
  const originalName = itemNameFromStore(originalItemId, freeText);
  const substituteName = substituteItemId ? itemNameFromStore(substituteItemId) : null;
  const qtyPart = qty != null ? ` (qty ${qty})` : '';
  if (substituteName && substituteItemId && substituteItemId !== originalItemId) {
    return {
      description: `${verb}${qtyPart}: ${substituteName} substituted for ${originalName}`,
      payload: { originalItemId, substituteItemId, originalName, substituteName },
    };
  }
  return {
    description: `${verb}${qtyPart}: ${originalName}`,
    payload: { originalItemId, originalName },
  };
}

async function assignRequest(
  requestId: string,
  assigneeId: string | null,
  step: AssignmentStep | null,
  notifyType: string,
  notifyMessage: string,
  lineId?: string | null,
) {
  const { error } = await supabase
    .from('stock_requests')
    .update({ assigned_to: assigneeId, assignment_step: step })
    .eq('id', requestId);

  if (error) throw new Error(error.message);

  if (assigneeId && notifyMessage) {
    const { useStockNotificationsStore } = await import('./stockNotificationsStore');
    await useStockNotificationsStore.getState().notify(
      assigneeId,
      requestId,
      notifyType,
      notifyMessage,
      lineId,
    );
  }
}

function lineApprovalNeedsRequesterReview(
  la: LineApprovalInput,
  requestedQty: number,
): boolean {
  if (la.status === 'unavailable') return true;
  if (la.substituteItemId) return true;
  if (la.approvedQty < requestedQty) return true;
  return false;
}

export interface CounterProposalInput {
  substituteItemId?: string | null;
  approvedQty?: number;
  unavailable?: boolean;
  reason?: string | null;
}

async function tryFinalizeOrReassign(requestId: string, ref: string): Promise<void> {
  const { data: allLines } = await supabase
    .from('stock_request_lines')
    .select('*')
    .eq('request_id', requestId);
  const lines = (allLines ?? []).map(r => toLine(r as DBStockRequestLine));

  if (canAutoApproveAfterNegotiation(lines)) {
    await finalizeRequestAfterNegotiation(requestId);
  } else if (!hasPendingNegotiation(lines)) {
    await reassignToManager(
      requestId,
      `${ref} — all responses received. Ready for your final approval.`,
    );
  }
}

function canRespondToProposal(
  pending: SubstituteStatus,
  userId: string,
  requesterId: string,
  reviewedBy: string | null,
  assignedTo: string | null,
): boolean {
  if (pending === 'pending_requester') return userId === requesterId;
  if (pending === 'pending_manager') {
    return userId === reviewedBy || userId === assignedTo;
  }
  return false;
}

function responderLabel(pending: SubstituteStatus): string {
  return pending === 'pending_requester' ? 'requester' : 'manager';
}

async function reassignToManager(requestId: string, message: string, lineId?: string | null) {
  const reqRow = await supabase
    .from('stock_requests')
    .select('request_number, reviewed_by')
    .eq('id', requestId)
    .single();

  await useStockRequestSettingsStore.getState().fetchSettings();
  const settings = useStockRequestSettingsStore.getState().settings;
  const managerId = reqRow.data?.reviewed_by
    ?? pickAssignee(defaultManagerIds(settings));

  if (managerId) {
    await assignRequest(requestId, managerId, 'review', 'assigned', message, lineId);
  }
}

async function finalizeRequestAfterNegotiation(requestId: string): Promise<void> {
  const { data: reqRow } = await supabase
    .from('stock_requests')
    .select('request_number, requester_id, reviewed_by')
    .eq('id', requestId)
    .single();

  const { data: lineRows } = await supabase
    .from('stock_request_lines')
    .select('*')
    .eq('request_id', requestId);

  const lines = (lineRows ?? []).map(r => toLine(r as DBStockRequestLine));
  if (!canAutoApproveAfterNegotiation(lines)) return;

  const activeLines = lines.filter(l => l.status !== 'cancelled');

  for (const line of activeLines) {
    if (line.status === 'approved' || line.status === 'unavailable') continue;

    const approvedQty = line.approvedQty ?? line.requestedQty;
    const updates: Record<string, unknown> = {};

    if (line.substituteStatus === 'accepted' && !line.substituteItemId) {
      updates.status = 'unavailable';
      updates.approved_qty = null;
    } else {
      updates.status = 'approved';
      updates.approved_qty = approvedQty;
      if (line.substituteItemId && line.substituteStatus === 'accepted') {
        updates.substitute_status = 'accepted';
      }
    }

    await supabase.from('stock_request_lines').update(updates).eq('id', line.id);
    await logEvent(
      requestId,
      updates.status === 'unavailable' ? 'line_unavailable' : 'line_approved',
      updates.status === 'unavailable'
        ? (line.approvalReason?.trim() || 'Marked unavailable')
        : `Approved qty ${approvedQty}`,
      line.id,
    );
  }

  const now = new Date().toISOString();
  const reviewerId = reqRow?.reviewed_by ?? null;
  await supabase
    .from('stock_requests')
    .update({
      status: 'approved',
      approved_at: now,
      reviewed_by: reviewerId,
      assignment_step: null,
      assigned_to: null,
    })
    .eq('id', requestId);

  await logEvent(requestId, 'approved', 'Request approved — all changes agreed');

  const ref = reqRow ? `SR-${reqRow.request_number}` : 'a request';
  await useStockRequestSettingsStore.getState().fetchSettings();
  const settings = useStockRequestSettingsStore.getState().settings;

  const ordererId = pickAssignee(defaultManagerIds(settings), reviewerId);
  if (ordererId) {
    await assignRequest(
      requestId,
      ordererId,
      'order',
      'assigned',
      `${ref} has been approved and assigned to you to place the order.`,
    );
  }

  if (reqRow?.requester_id) {
    const { useStockNotificationsStore } = await import('./stockNotificationsStore');
    await useStockNotificationsStore.getState().notify(
      reqRow.requester_id,
      requestId,
      'approved',
      `Your request ${ref} has been approved.`,
    );
  }
}

// ── Store ─────────────────────────────────────────────────────────────────────

interface StockRequestState {
  requests: StockRequest[];
  selectedRequest: StockRequest | null;
  isLoading: boolean;
  error: string | null;

  fetchRequests: () => Promise<void>;
  loadRequestDetail: (id: string) => Promise<StockRequest | null>;
  fetchRequestDetail: (id: string) => Promise<StockRequest | null>;
  clearSelected: () => void;

  createRequest: (input: {
    warehouseId: string;
    storageAreaId: string | null;
    priority: 'low' | 'medium' | 'high';
    notes?: string | null;
    siteId?: string | null;
    jobId?: string | null;
    lines: NewRequestLineInput[];
  }) => Promise<StockRequest | null>;

  /** Append lines to an existing draft request (requester only via RLS). */
  addDraftLines: (requestId: string, lines: NewRequestLineInput[]) => Promise<StockRequestLine[] | null>;

  /** Update qty (and optional item fields) on a draft line. */
  updateDraftLine: (
    requestId: string,
    lineId: string,
    patch: Partial<Pick<NewRequestLineInput, 'lineType' | 'itemId' | 'freeTextDescription' | 'requestedQty' | 'condition'>>,
  ) => Promise<boolean>;

  /** Remove a draft line; deletes the draft request if it becomes empty. */
  removeDraftLine: (requestId: string, lineId: string) => Promise<boolean>;

  /** Find the current user's most recent draft for a destination (sheet cart). */
  findMyActiveDraft: (warehouseId: string, storageAreaId: string | null) => StockRequest | null;

  /**
   * Sheet (By item) inline edits — updates line fields and rolls up request status.
   * Line status is driven by workflow actions (submit, approve, order, receive), not this patch.
   */
  updateSheetLine: (
    requestId: string,
    lineId: string,
    patch: {
      requestedQty?: number;
      approvedQty?: number | null;
      supplier?: string | null;
      expectedDate?: string | null;
      poReference?: string | null;
      itemId?: string | null;
      freeTextDescription?: string | null;
      lineType?: 'catalog' | 'free_text';
      condition?: 'new' | 'used';
      substituteItemId?: string | null;
      warehouseId?: string;
      storageAreaId?: string | null;
    },
  ) => Promise<boolean>;

  /** Open requests (not draft/completed/cancelled) that already include this catalogue item. */
  findOpenItemConflicts: (itemId: string) => Promise<OpenItemConflict[]>;

  submitRequest: (id: string) => Promise<boolean>;
  saveLineReviewDraft: (requestId: string, input: LineReviewDraftInput) => Promise<boolean>;
  sendForRequesterReview: (id: string, lineApprovals: LineApprovalInput[]) => Promise<boolean>;
  approveRequest: (id: string, lineApprovals: LineApprovalInput[]) => Promise<boolean>;
  approveSheetLines: (requestId: string, lines: LineApproveInput[]) => Promise<boolean>;
  markOrdered: (id: string, lineOrders?: LineOrderInput[]) => Promise<boolean>;
  receiveLines: (id: string, receipts: LineReceiveInput[]) => Promise<{ success: boolean; message: string }>;
  reportReceiveIssue: (
    requestId: string,
    input: ReceiveIssueInput,
  ) => Promise<{ success: boolean; message: string }>;
  acceptSubstitute: (requestId: string, lineId: string) => Promise<boolean>;
  declineSubstitute: (requestId: string, lineId: string) => Promise<boolean>;
  counterPropose: (requestId: string, lineId: string, input: CounterProposalInput) => Promise<boolean>;
  splitLine: (requestId: string, lineId: string, splitQty: number, overrideItemId?: string | null) => Promise<boolean>;
  disputeLine: (requestId: string, lineId: string, reason: string) => Promise<boolean>;
  resolveDispute: (requestId: string, lineId: string, resolution: 'accept' | 'reorder' | 'cancel') => Promise<boolean>;
  cancelLine: (requestId: string, lineId: string, reason: string) => Promise<boolean>;
  backorderLine: (requestId: string, lineId: string, note: string, expectedDate?: string) => Promise<boolean>;
  cancelRequest: (id: string) => Promise<boolean>;
  deleteRequest: (id: string) => Promise<boolean>;
  updateRequestLinks: (requestId: string, links: { siteId?: string | null; jobId?: string | null }) => Promise<boolean>;
  fetchRequestsForJob: (jobId: string) => Promise<StockRequest[]>;
  applyRemoteRow: (
    table: 'stock_requests' | 'stock_request_lines',
    event: 'INSERT' | 'UPDATE' | 'DELETE',
    row: Record<string, unknown> | null,
  ) => void;
}

/**
 * Destination is stored on the request header. Changing dest on one draft line
 * among several must reparent that line onto a draft for the new warehouse/area
 * instead of rewriting the shared header.
 */
async function moveDraftLineToDestination(
  get: () => StockRequestState,
  set: (
    partial:
      | Partial<StockRequestState>
      | ((state: StockRequestState) => Partial<StockRequestState>),
  ) => void,
  source: StockRequest,
  line: StockRequestLine,
  warehouseId: string,
  storageAreaId: string | null,
): Promise<StockRequest | null> {
  const user = useAuthStore.getState().user;
  const collectionId = getActiveCollectionId();
  if (!user || !collectionId) {
    set({ error: 'Not signed in or no collection loaded' });
    return null;
  }

  let target = get().requests.find(r =>
    r.status === 'draft'
    && r.requesterId === user.id
    && r.warehouseId === warehouseId
    && (r.storageAreaId ?? null) === (storageAreaId ?? null)
    && r.id !== source.id,
  ) ?? null;

  let createdEmptyId: string | null = null;
  if (!target) {
    const { data: header, error: headerErr } = await supabase
      .from('stock_requests')
      .insert({
        collection_id: collectionId,
        requester_id: user.id,
        warehouse_id: warehouseId,
        storage_area_id: storageAreaId,
        priority: source.priority,
        site_id: source.siteId,
        job_id: source.jobId,
        status: 'draft',
      })
      .select()
      .single();

    if (headerErr || !header) {
      set({ error: headerErr?.message ?? 'Failed to move item to destination' });
      return null;
    }

    createdEmptyId = header.id as string;
    await logEvent(header.id, 'created', 'Request created');
    target = toRequest(header as DBStockRequest, []);
    const created = target;
    set(s => ({ requests: [created, ...s.requests] }));
  }

  const maxSort = (target.lines ?? []).reduce((m, l) => Math.max(m, l.sortOrder), -1);
  const { data: movedRows, error: moveErr } = await supabase
    .from('stock_request_lines')
    .update({ request_id: target.id, sort_order: maxSort + 1 })
    .eq('id', line.id)
    .eq('request_id', source.id)
    .select('id');

  if (moveErr || !movedRows?.length) {
    if (createdEmptyId) {
      await supabase.from('stock_requests').delete().eq('id', createdEmptyId);
      set(s => ({
        requests: s.requests.filter(r => r.id !== createdEmptyId),
        error: moveErr?.message ?? 'Could not move item to destination.',
      }));
    } else {
      set({ error: moveErr?.message ?? 'Could not move item to destination.' });
    }
    return null;
  }

  const movedLine: StockRequestLine = {
    ...line,
    requestId: target.id,
    sortOrder: maxSort + 1,
    updatedAt: new Date(),
  };
  const targetId = target.id;

  set(s => ({
    requests: s.requests.map(r => {
      if (r.id === source.id) {
        return {
          ...r,
          lines: (r.lines ?? []).filter(l => l.id !== line.id),
          updatedAt: new Date(),
        };
      }
      if (r.id === targetId) {
        return {
          ...r,
          lines: [...(r.lines ?? []).filter(l => l.id !== line.id), movedLine],
          updatedAt: new Date(),
        };
      }
      return r;
    }),
    selectedRequest: s.selectedRequest?.id === source.id
      ? {
          ...s.selectedRequest,
          lines: (s.selectedRequest.lines ?? []).filter(l => l.id !== line.id),
        }
      : s.selectedRequest?.id === targetId
        ? {
            ...s.selectedRequest,
            lines: [...(s.selectedRequest.lines ?? []).filter(l => l.id !== line.id), movedLine],
          }
        : s.selectedRequest,
  }));

  await logEvent(
    targetId,
    'destination_changed',
    'Item moved to this destination',
    line.id,
    { fromRequestId: source.id, warehouseId, storageAreaId },
  );

  return get().requests.find(r => r.id === targetId) ?? null;
}

export const useStockRequestStore = create<StockRequestState>((set, get) => ({
  requests: [],
  selectedRequest: null,
  isLoading: false,
  error: null,

  fetchRequests: async () => {
    const collectionId = getActiveCollectionId();
    if (!collectionId) {
      set({ requests: [], isLoading: false });
      return;
    }
    set({ isLoading: true, error: null });
    const { data, error } = await supabase
      .from('stock_requests')
      .select('*')
      .eq('collection_id', collectionId)
      .order('created_at', { ascending: false });

    if (error) {
      set({ isLoading: false, error: error.message });
      return;
    }

    const requestRows = (data ?? []) as DBStockRequest[];
    const requestIds = requestRows.map(row => row.id);
    const [linesRes, eventsRes] = requestIds.length > 0
      ? await Promise.all([
          supabase
            .from('stock_request_lines')
            .select('*')
            .in('request_id', requestIds)
            .order('sort_order', { ascending: true }),
          supabase
            .from('stock_request_events')
            .select('*')
            .in('request_id', requestIds)
            .order('created_at', { ascending: false }),
        ])
      : [{ data: [] }, { data: [] }];

    const linesByRequest = new Map<string, StockRequestLine[]>();
    for (const row of (linesRes.data ?? []) as DBStockRequestLine[]) {
      const line = toLine(row);
      const lines = linesByRequest.get(line.requestId) ?? [];
      lines.push(line);
      linesByRequest.set(line.requestId, lines);
    }

    const eventsByRequest = new Map<string, StockRequestEvent[]>();
    for (const row of (eventsRes.data ?? []) as DBStockRequestEvent[]) {
      const event = toEvent(row);
      const events = eventsByRequest.get(event.requestId) ?? [];
      events.push(event);
      eventsByRequest.set(event.requestId, events);
    }

    const requests = requestRows.map(row => toRequest(
      row,
      linesByRequest.get(row.id) ?? [],
      eventsByRequest.get(row.id) ?? [],
    ));
    set({ requests, isLoading: false });
  },

  fetchRequestDetail: async (id: string) => {
    set({ isLoading: true, error: null });
    const request = await get().loadRequestDetail(id);
    if (!request) {
      set({ isLoading: false, error: 'Request not found' });
      return null;
    }
    set({ selectedRequest: request, isLoading: false });
    return request;
  },

  loadRequestDetail: async (id: string) => {
    const [reqRes, linesRes, eventsRes] = await Promise.all([
      supabase.from('stock_requests').select('*').eq('id', id).single(),
      supabase.from('stock_request_lines').select('*').eq('request_id', id).order('sort_order', { ascending: true }),
      supabase.from('stock_request_events').select('*').eq('request_id', id).order('created_at', { ascending: false }),
    ]);

    if (reqRes.error || !reqRes.data) return null;

    const lines = (linesRes.data ?? []).map(r => toLine(r as DBStockRequestLine));
    const events = (eventsRes.data ?? []).map(r => toEvent(r as DBStockRequestEvent));
    return toRequest(reqRes.data as DBStockRequest, lines, events);
  },

  clearSelected: () => set({ selectedRequest: null }),

  findOpenItemConflicts: async (itemId: string) => {
    const trimmed = itemId.trim();
    if (!trimmed) return [];

    const openStatuses = new Set([
      'submitted',
      'approved',
      'partially_ordered',
      'ordered',
      'partially_received',
    ]);
    const skipLine = new Set(['cancelled', 'unavailable']);

    const fromLoadedRequests = (): OpenItemConflict[] => {
      const conflicts: OpenItemConflict[] = [];
      const seen = new Set<string>();
      for (const req of get().requests) {
        if (!openStatuses.has(req.status) || seen.has(req.id)) continue;
        const match = (req.lines ?? []).find(line =>
          !skipLine.has(line.status)
          && (line.itemId === trimmed || line.substituteItemId === trimmed),
        );
        if (!match) continue;
        seen.add(req.id);
        conflicts.push({
          requestId: req.id,
          requestNumber: req.requestNumber,
          status: req.status,
          lineStatus: match.status,
          requestedQty: match.requestedQty,
        });
      }
      return conflicts;
    };

    // Instant path: list view already loaded requests + lines into the store.
    if (get().requests.length > 0) {
      return fromLoadedRequests();
    }

    // Cold-start fallback (wizard opened before fetch finished).
    const collectionId = getActiveCollectionId();
    if (!collectionId) return [];

    const { data: openReqs, error: reqErr } = await supabase
      .from('stock_requests')
      .select('id, request_number, status')
      .eq('collection_id', collectionId)
      .not('status', 'in', '(draft,completed,cancelled)');

    if (reqErr || !openReqs || openReqs.length === 0) return [];

    const requestIds = openReqs.map(r => r.id as string);
    const { data: lines, error: lineErr } = await supabase
      .from('stock_request_lines')
      .select('request_id, status, requested_qty, item_id, substitute_item_id')
      .in('request_id', requestIds)
      .or(`item_id.eq.${trimmed},substitute_item_id.eq.${trimmed}`)
      .not('status', 'in', '(cancelled,unavailable)');

    if (lineErr || !lines || lines.length === 0) return [];

    const byId = new Map(
      openReqs.map(r => [r.id as string, r as { id: string; request_number: number; status: string }]),
    );

    const conflicts: OpenItemConflict[] = [];
    const seen = new Set<string>();
    for (const line of lines) {
      const req = byId.get(line.request_id as string);
      if (!req || seen.has(req.id)) continue;
      seen.add(req.id);
      conflicts.push({
        requestId: req.id,
        requestNumber: req.request_number,
        status: req.status as RequestStatus,
        lineStatus: line.status as LineStatus,
        requestedQty: line.requested_qty as number,
      });
    }
    return conflicts;
  },

  createRequest: async (input) => {
    const collectionId = getActiveCollectionId();
    const user = useAuthStore.getState().user;
    if (!collectionId || !user) {
      set({ error: 'Not signed in or no collection loaded' });
      return null;
    }
    if (input.lines.length === 0) {
      set({ error: 'Add at least one line' });
      return null;
    }

    set({ error: null });
    const { data: header, error: headerErr } = await supabase
      .from('stock_requests')
      .insert({
        collection_id: collectionId,
        requester_id: user.id,
        warehouse_id: input.warehouseId,
        storage_area_id: input.storageAreaId,
        priority: input.priority,
        notes: input.notes?.trim() || null,
        site_id: input.siteId ?? null,
        job_id: input.jobId ?? null,
        status: 'draft',
      })
      .select()
      .single();

    if (headerErr || !header) {
      set({ error: headerErr?.message ?? 'Failed to create request' });
      return null;
    }

    const lineRows = input.lines.map((line, idx) => ({
      request_id: header.id,
      line_type: line.lineType,
      item_id: line.lineType === 'catalog' ? line.itemId : null,
      free_text_description: line.lineType === 'free_text' ? line.freeTextDescription?.trim() : null,
      requested_qty: line.requestedQty,
      condition: line.condition,
      status: 'draft',
      sort_order: idx,
    }));

    const { data: insertedLines, error: linesErr } = await supabase
      .from('stock_request_lines')
      .insert(lineRows)
      .select();

    if (linesErr) {
      await supabase.from('stock_requests').delete().eq('id', header.id);
      set({ error: linesErr.message });
      return null;
    }

    await logEvent(header.id, 'created', 'Request created');
    const lines = (insertedLines ?? []).map(r => toLine(r as DBStockRequestLine));
    const request = toRequest(header as DBStockRequest, lines);
    markOwnWrite('stock_requests', request.id);
    lines.forEach((l) => markOwnWrite('stock_request_lines', l.id));
    set(s => ({ requests: [request, ...s.requests] }));
    return request;
  },

  findMyActiveDraft: (warehouseId, storageAreaId) => {
    const user = useAuthStore.getState().user;
    if (!user || !warehouseId) return null;
    const drafts = get().requests
      .filter(r =>
        r.status === 'draft'
        && r.requesterId === user.id
        && r.warehouseId === warehouseId
        && (r.storageAreaId ?? null) === (storageAreaId ?? null),
      )
      .sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime());
    return drafts[0] ?? null;
  },

  addDraftLines: async (requestId, lines) => {
    if (lines.length === 0) {
      set({ error: 'Add at least one line' });
      return null;
    }

    const existing = get().requests.find(r => r.id === requestId);
    if (!existing || existing.status !== 'draft') {
      set({ error: 'Can only add lines to a draft request' });
      return null;
    }

    const maxSort = (existing.lines ?? []).reduce((m, l) => Math.max(m, l.sortOrder), -1);
    const lineRows = lines.map((line, idx) => ({
      request_id: requestId,
      line_type: line.lineType,
      item_id: line.lineType === 'catalog' ? line.itemId : null,
      free_text_description: line.lineType === 'free_text' ? line.freeTextDescription?.trim() : null,
      requested_qty: line.requestedQty,
      condition: line.condition,
      status: 'draft',
      sort_order: maxSort + 1 + idx,
    }));

    set({ error: null });
    const { data: insertedLines, error: linesErr } = await supabase
      .from('stock_request_lines')
      .insert(lineRows)
      .select();

    if (linesErr) {
      set({ error: linesErr.message });
      return null;
    }

    const added = (insertedLines ?? []).map(r => toLine(r as DBStockRequestLine));
    set(s => ({
      requests: s.requests.map(r => {
        if (r.id !== requestId) return r;
        return {
          ...r,
          lines: [...(r.lines ?? []), ...added],
          updatedAt: new Date(),
        };
      }),
    }));
    return added;
  },

  updateDraftLine: async (requestId, lineId, patch) => {
    const existing = get().requests.find(r => r.id === requestId);
    if (!existing || existing.status !== 'draft') {
      set({ error: 'Can only edit lines on a draft request' });
      return false;
    }

    const updates: Record<string, unknown> = {};
    if (patch.lineType !== undefined) updates.line_type = patch.lineType;
    if (patch.itemId !== undefined) updates.item_id = patch.itemId;
    if (patch.freeTextDescription !== undefined) {
      updates.free_text_description = patch.freeTextDescription?.trim() || null;
    }
    if (patch.requestedQty !== undefined) updates.requested_qty = patch.requestedQty;
    if (patch.condition !== undefined) updates.condition = patch.condition;

    if (Object.keys(updates).length === 0) return true;

    set({ error: null });
    const { error } = await supabase
      .from('stock_request_lines')
      .update(updates)
      .eq('id', lineId)
      .eq('request_id', requestId);

    if (error) {
      set({ error: error.message });
      return false;
    }

    set(s => ({
      requests: s.requests.map(r => {
        if (r.id !== requestId) return r;
        return {
          ...r,
          lines: (r.lines ?? []).map(l => {
            if (l.id !== lineId) return l;
            return {
              ...l,
              lineType: patch.lineType ?? l.lineType,
              itemId: patch.itemId !== undefined ? (patch.itemId ?? null) : l.itemId,
              freeTextDescription: patch.freeTextDescription !== undefined
                ? (patch.freeTextDescription?.trim() || null)
                : l.freeTextDescription,
              requestedQty: patch.requestedQty ?? l.requestedQty,
              condition: patch.condition ?? l.condition,
              updatedAt: new Date(),
            };
          }),
          updatedAt: new Date(),
        };
      }),
    }));
    return true;
  },

  removeDraftLine: async (requestId, lineId) => {
    const existing = get().requests.find(r => r.id === requestId);
    if (!existing || existing.status !== 'draft') {
      set({ error: 'Can only remove lines from a draft request' });
      return false;
    }

    set({ error: null });
    const { error } = await supabase
      .from('stock_request_lines')
      .delete()
      .eq('id', lineId)
      .eq('request_id', requestId);

    if (error) {
      set({ error: error.message });
      return false;
    }

    const remaining = (existing.lines ?? []).filter(l => l.id !== lineId);
    if (remaining.length === 0) {
      return get().deleteRequest(requestId);
    }

    set(s => ({
      requests: s.requests.map(r => {
        if (r.id !== requestId) return r;
        return { ...r, lines: remaining, updatedAt: new Date() };
      }),
    }));
    return true;
  },

  updateSheetLine: async (requestId, lineId, patch) => {
    const existing = get().requests.find(r => r.id === requestId);
    if (!existing) {
      set({ error: 'Request not found' });
      return false;
    }
    const line = (existing.lines ?? []).find(l => l.id === lineId);
    if (!line) {
      set({ error: 'Line not found' });
      return false;
    }

    set({ error: null });

    let workingRequestId = requestId;
    let working = existing;
    const sourceRequestId = requestId;
    const nextWarehouseId = patch.warehouseId ?? existing.warehouseId;
    const nextStorageAreaId = patch.storageAreaId !== undefined
      ? patch.storageAreaId
      : existing.storageAreaId;
    const destActuallyChanged = (
      (patch.warehouseId !== undefined || patch.storageAreaId !== undefined)
      && (
        nextWarehouseId !== existing.warehouseId
        || (nextStorageAreaId ?? null) !== (existing.storageAreaId ?? null)
      )
    );

    if (destActuallyChanged) {
      const otherDraftLines = (existing.lines ?? []).filter(l => l.id !== lineId).length;
      if (existing.status === 'draft' && otherDraftLines > 0) {
        const movedTo = await moveDraftLineToDestination(
          get,
          set,
          existing,
          line,
          nextWarehouseId,
          nextStorageAreaId ?? null,
        );
        if (!movedTo) return false;
        workingRequestId = movedTo.id;
        working = movedTo;
      } else {
        const headerPatch: Record<string, unknown> = {
          warehouse_id: nextWarehouseId,
          storage_area_id: nextStorageAreaId ?? null,
        };
        const { error: destErr } = await supabase
          .from('stock_requests')
          .update(headerPatch)
          .eq('id', requestId);
        if (destErr) {
          set({ error: destErr.message });
          return false;
        }
        working = {
          ...existing,
          warehouseId: nextWarehouseId,
          storageAreaId: nextStorageAreaId ?? null,
        };
      }
    }

    const updates: Record<string, unknown> = {};
    if (patch.lineType !== undefined) updates.line_type = patch.lineType;
    if (patch.itemId !== undefined) updates.item_id = patch.itemId;
    if (patch.freeTextDescription !== undefined) {
      updates.free_text_description = patch.freeTextDescription?.trim() || null;
    }
    if (patch.requestedQty !== undefined) updates.requested_qty = patch.requestedQty;
    if (patch.approvedQty !== undefined) updates.approved_qty = patch.approvedQty;
    if (patch.supplier !== undefined) updates.supplier = patch.supplier?.trim() || null;
    if (patch.expectedDate !== undefined) updates.expected_date = patch.expectedDate || null;
    if (patch.poReference !== undefined) updates.po_reference = patch.poReference?.trim() || null;
    if (patch.condition !== undefined) updates.condition = patch.condition;
    if (patch.substituteItemId !== undefined) {
      updates.substitute_item_id = patch.substituteItemId || null;
      // Sheet-side assign: clear negotiation state unless a formal review send follows
      if (!patch.substituteItemId) {
        updates.substitute_status = null;
      }
    }

    if (Object.keys(updates).length > 0) {
      const { data: updatedRows, error } = await supabase
        .from('stock_request_lines')
        .update(updates)
        .eq('id', lineId)
        .eq('request_id', workingRequestId)
        .select('id');
      if (error) {
        set({ error: error.message });
        return false;
      }
      if (!updatedRows?.length) {
        set({ error: 'Could not update line (no permission or line not found).' });
        return false;
      }
    }

    const rollupRequest = async (req: StockRequest) => {
      const { data: lineRows } = await supabase
        .from('stock_request_lines')
        .select('*')
        .eq('request_id', req.id)
        .order('sort_order', { ascending: true });

      const nextLines = (lineRows ?? []).map(r => toLine(r as DBStockRequestLine));
      const nextStatus = deriveRequestStatus(nextLines);

      const headerUpdates: Record<string, unknown> = { status: nextStatus };
      if (nextStatus === 'submitted' && !req.submittedAt) {
        headerUpdates.submitted_at = new Date().toISOString();
      }
      if (nextStatus === 'ordered' && !req.orderedAt) {
        headerUpdates.ordered_at = new Date().toISOString();
      }
      if (nextStatus === 'completed' && !req.completedAt) {
        headerUpdates.completed_at = new Date().toISOString();
      }
      if (
        (nextStatus === 'partially_received' || nextStatus === 'completed')
        && !req.firstReceivedAt
      ) {
        headerUpdates.first_received_at = new Date().toISOString();
      }

      const { error: headerErr } = await supabase
        .from('stock_requests')
        .update(headerUpdates)
        .eq('id', req.id);
      if (headerErr) {
        set({ error: headerErr.message });
        return null;
      }
      return { lines: nextLines, nextStatus };
    };

    const rolled = await rollupRequest(working);
    if (!rolled) return false;

    if (sourceRequestId !== workingRequestId) {
      const sourceNow = get().requests.find(r => r.id === sourceRequestId);
      if (sourceNow) {
        const sourceRolled = await rollupRequest(sourceNow);
        if (!sourceRolled) return false;
      }
    }

    // Optimistic merge so the sheet/drawer update immediately (don't wait on full refetch).
    const mergeRequest = (r: StockRequest): StockRequest => {
      if (r.id === workingRequestId) {
        return {
          ...r,
          status: rolled.nextStatus,
          warehouseId: destActuallyChanged ? nextWarehouseId : r.warehouseId,
          storageAreaId: destActuallyChanged ? (nextStorageAreaId ?? null) : r.storageAreaId,
          lines: rolled.lines.length > 0
            ? rolled.lines
            : (r.lines ?? []).map(l => {
                if (l.id !== lineId) return l;
                return {
                  ...l,
                  ...(patch.lineType !== undefined ? { lineType: patch.lineType } : {}),
                  ...(patch.itemId !== undefined ? { itemId: patch.itemId } : {}),
                  ...(patch.freeTextDescription !== undefined ? { freeTextDescription: patch.freeTextDescription } : {}),
                  ...(patch.requestedQty !== undefined ? { requestedQty: patch.requestedQty } : {}),
                  ...(patch.approvedQty !== undefined ? { approvedQty: patch.approvedQty } : {}),
                  ...(patch.supplier !== undefined ? { supplier: patch.supplier } : {}),
                  ...(patch.expectedDate !== undefined ? { expectedDate: patch.expectedDate } : {}),
                  ...(patch.poReference !== undefined ? { poReference: patch.poReference } : {}),
                  ...(patch.condition !== undefined ? { condition: patch.condition } : {}),
                  ...(patch.substituteItemId !== undefined ? { substituteItemId: patch.substituteItemId } : {}),
                  updatedAt: new Date(),
                };
              }),
        };
      }
      if (r.id === sourceRequestId && sourceRequestId !== workingRequestId) {
        return {
          ...r,
          lines: (r.lines ?? []).filter(l => l.id !== lineId),
        };
      }
      return r;
    };

    set(state => ({
      requests: state.requests.map(mergeRequest),
      selectedRequest: state.selectedRequest ? mergeRequest(state.selectedRequest) : null,
    }));

    void get().fetchRequests();
    return true;
  },

  submitRequest: async (id: string) => {
    const user = useAuthStore.getState().user;
    if (!user) return false;

    const { data: lines } = await supabase
      .from('stock_request_lines')
      .select('*')
      .eq('request_id', id);

    const incomplete = (lines ?? []).filter(l =>
      (l.line_type === 'catalog' && !l.item_id)
      || (l.line_type === 'free_text' && !String(l.free_text_description ?? '').trim()),
    );
    if (incomplete.length > 0) {
      set({ error: 'Fill in all items before submitting.' });
      return false;
    }
    if (!lines?.length) {
      set({ error: 'Add at least one item before submitting.' });
      return false;
    }

    const lineUpdates = (lines ?? []).map(l => ({
      id: l.id,
      status: l.line_type === 'free_text' ? 'pending_resolution' : 'submitted',
    }));

    for (const lu of lineUpdates) {
      await supabase.from('stock_request_lines').update({ status: lu.status }).eq('id', lu.id);
    }

    const now = new Date().toISOString();

    await useStockRequestSettingsStore.getState().fetchSettings();
    const settings = useStockRequestSettingsStore.getState().settings;
    const approverId = pickAssignee(defaultManagerIds(settings), user.id);

    const { error } = await supabase
      .from('stock_requests')
      .update({ status: 'submitted', submitted_at: now })
      .eq('id', id);

    if (error) {
      set({ error: error.message });
      return false;
    }

    await logEvent(id, 'submitted', 'Request submitted for review');
    await get().fetchRequests();
    await get().fetchRequestDetail(id);

    const req = get().selectedRequest;
    const ref = req ? `SR-${req.requestNumber}` : 'a request';
    if (approverId) {
      await assignRequest(
        id,
        approverId,
        'review',
        'assigned',
        `${ref} has been assigned to you for review.`,
      );
    } else {
      await supabase
        .from('stock_requests')
        .update({ assignment_step: 'review', assigned_to: null })
        .eq('id', id);
    }
    return true;
  },

  saveLineReviewDraft: async (requestId: string, input: LineReviewDraftInput) => {
    const user = useAuthStore.getState().user;
    if (!user) return false;

    const { data: reqRow, error: reqError } = await supabase
      .from('stock_requests')
      .select('status')
      .eq('id', requestId)
      .single();
    if (reqError || !reqRow) {
      set({ error: reqError?.message ?? 'Request not found' });
      return false;
    }
    if (!['submitted', 'approved'].includes(reqRow.status)) {
      set({ error: 'Request is not in review.' });
      return false;
    }

    const { data: lineRow, error: lineError } = await supabase
      .from('stock_request_lines')
      .select('requested_qty, substitute_status')
      .eq('id', input.lineId)
      .single();
    if (lineError || !lineRow) {
      set({ error: lineError?.message ?? 'Line not found' });
      return false;
    }
    if (lineRow.substitute_status) return true;

    const updates: Record<string, unknown> = {};

    if (input.matchItemId !== undefined) {
      updates.item_id = input.matchItemId || null;
    }
    if (input.resolutionNotes !== undefined) {
      updates.resolution_notes = input.resolutionNotes?.trim() || null;
    }

    if (input.disposition === null) {
      updates.approved_qty = null;
      updates.approval_reason = null;
      updates.substitute_item_id = null;
    } else if (input.disposition === 'unavailable') {
      updates.approval_reason = input.unavailableReason?.trim() || null;
      updates.approved_qty = null;
      updates.substitute_item_id = null;
    } else if (input.disposition === 'substitute') {
      updates.substitute_item_id = input.substituteItemId ?? null;
      const qty = input.approvedQty ?? lineRow.requested_qty;
      updates.approved_qty = qty > 0 ? qty : null;
      updates.approval_reason = input.approvalReason?.trim() || null;
    } else if (input.disposition === 'approve') {
      const qty = input.approvedQty ?? lineRow.requested_qty;
      updates.approved_qty = qty > 0 ? qty : null;
      updates.approval_reason = input.approvalReason?.trim() || null;
      updates.substitute_item_id = null;
    }

    if (Object.keys(updates).length === 0) return true;

    const { error: updateError } = await supabase
      .from('stock_request_lines')
      .update(updates)
      .eq('id', input.lineId);
    if (updateError) {
      set({ error: updateError.message });
      return false;
    }

    const detail = await get().loadRequestDetail(requestId);
    if (detail) {
      set(state => ({
        requests: state.requests.map(r => (r.id === requestId ? detail : r)),
        selectedRequest: state.selectedRequest?.id === requestId ? detail : state.selectedRequest,
      }));
    }
    return true;
  },

  sendForRequesterReview: async (id: string, lineApprovals: LineApprovalInput[]) => {
    const user = useAuthStore.getState().user;
    if (!user) return false;

    const { data: existingLines } = await supabase
      .from('stock_request_lines')
      .select('*')
      .eq('request_id', id);
    const lineById = new Map((existingLines ?? []).map(l => [l.id, l]));

    let anyNeedsReview = false;

    for (const la of lineApprovals) {
      const row = lineById.get(la.lineId);
      if (!row) continue;
      const needsReview = lineApprovalNeedsRequesterReview(la, row.requested_qty);

      const updates: Record<string, unknown> = {
        approval_reason: la.approvalReason?.trim() || null,
        approved_qty: la.status === 'unavailable' ? null : (la.approvedQty > 0 ? la.approvedQty : null),
      };
      if (la.itemId) updates.item_id = la.itemId;
      if (la.resolutionNotes) updates.resolution_notes = la.resolutionNotes.trim();

      if (needsReview) {
        anyNeedsReview = true;
        updates.status = 'submitted';
        updates.substitute_status = 'pending_requester';
        updates.substitute_item_id = la.substituteItemId ?? null;
      } else {
        updates.status = 'submitted';
        updates.substitute_status = null;
      }

      const { error: lineUpdateError } = await supabase
        .from('stock_request_lines')
        .update(updates)
        .eq('id', la.lineId);
      if (lineUpdateError) {
        set({ error: `Failed to update line: ${lineUpdateError.message}` });
        return false;
      }

      const desc = needsReview
        ? la.status === 'unavailable'
          ? `Unavailable proposed: ${la.approvalReason?.trim() || 'Marked unavailable'}`
          : la.substituteItemId
            ? substituteActivityDesc('Substitute proposed', row.item_id, la.substituteItemId, row.free_text_description, la.approvedQty).description
            : `Qty change proposed: ${la.approvedQty} (requested ${row.requested_qty})`
        : `Line noted during review (qty ${la.approvedQty})`;

      const eventPayload = needsReview && la.substituteItemId
        ? substituteActivityDesc('Substitute proposed', row.item_id, la.substituteItemId, row.free_text_description, la.approvedQty).payload
        : {};

      await logEvent(id, needsReview ? 'sent_for_requester_review' : 'line_review_draft', desc, la.lineId, eventPayload);
    }

    if (!anyNeedsReview) {
      set({ error: 'No changes require requester review. Use Approve request instead.' });
      return false;
    }

    const { error: requestUpdateError } = await supabase
      .from('stock_requests')
      .update({ status: 'submitted', reviewed_by: user.id })
      .eq('id', id);
    if (requestUpdateError) {
      set({ error: requestUpdateError.message });
      return false;
    }

    const reqRow = await supabase
      .from('stock_requests')
      .select('requester_id, request_number')
      .eq('id', id)
      .single();
    const ref = reqRow.data ? `SR-${reqRow.data.request_number}` : 'a request';

    try {
      if (reqRow.data?.requester_id) {
        await assignRequest(
          id,
          reqRow.data.requester_id,
          'requester',
          'review',
          `${ref} has proposed changes that need your review.`,
        );
      }
    } catch (assignErr) {
      set({ error: assignErr instanceof Error ? assignErr.message : 'Failed to assign request' });
      return false;
    }

    await get().fetchRequests();
    await get().fetchRequestDetail(id);
    return true;
  },

  approveSheetLines: async (requestId, lines) => {
    const user = useAuthStore.getState().user;
    if (!user) return false;
    if (!lines.length) {
      set({ error: 'Select at least one line to approve.' });
      return false;
    }

    const existing = get().requests.find(r => r.id === requestId);
    if (!existing) {
      set({ error: 'Request not found' });
      return false;
    }

    set({ error: null });
    const now = new Date().toISOString();

    for (const entry of lines) {
      const line = (existing.lines ?? []).find(l => l.id === entry.lineId);
      if (!line) {
        set({ error: 'Line not found' });
        return false;
      }
      if (!['draft', 'submitted', 'pending_resolution'].includes(line.status)) {
        set({ error: 'Line is not ready to approve.' });
        return false;
      }
      const qty = Math.max(1, entry.approvedQty);
      const { error } = await supabase
        .from('stock_request_lines')
        .update({
          status: 'approved',
          approved_qty: qty,
        })
        .eq('id', entry.lineId)
        .eq('request_id', requestId);
      if (error) {
        set({ error: error.message });
        return false;
      }
      await logEvent(requestId, 'line_approved', `Approved qty ${qty}`, entry.lineId, { approved_qty: qty });
    }

    const { data: lineRows } = await supabase
      .from('stock_request_lines')
      .select('*')
      .eq('request_id', requestId)
      .order('sort_order', { ascending: true });
    const nextLines = (lineRows ?? []).map(r => toLine(r as DBStockRequestLine));
    const nextStatus = deriveRequestStatus(nextLines);
    const headerUpdates: Record<string, unknown> = { status: nextStatus, updated_at: now };
    if (nextStatus === 'submitted' && !existing.submittedAt) headerUpdates.submitted_at = now;
    if (nextStatus === 'approved') {
      if (!existing.approvedAt) headerUpdates.approved_at = now;
      headerUpdates.reviewed_by = user.id;
    }
    if (existing.status === 'draft' && nextStatus !== 'draft' && !existing.submittedAt) {
      headerUpdates.submitted_at = now;
    }

    const { error: headerErr } = await supabase
      .from('stock_requests')
      .update(headerUpdates)
      .eq('id', requestId);
    if (headerErr) {
      set({ error: headerErr.message });
      return false;
    }

    await get().fetchRequests();
    await get().fetchRequestDetail(requestId);
    return true;
  },

  approveRequest: async (id: string, lineApprovals: LineApprovalInput[]) => {
    const user = useAuthStore.getState().user;
    if (!user) return false;

    const { data: existingLines } = await supabase
      .from('stock_request_lines')
      .select('*')
      .eq('request_id', id);

    if (hasPendingNegotiation((existingLines ?? []).map(r => toLine(r as DBStockRequestLine)))) {
      set({ error: 'All proposals must be resolved before approving the request.' });
      return false;
    }

    const lineById = new Map((existingLines ?? []).map(l => [l.id, l]));

    for (const la of lineApprovals) {
      const row = lineById.get(la.lineId);
      if (!row) continue;

      if (lineApprovalNeedsRequesterReview(la, row.requested_qty)) {
        set({ error: 'Use Send for review when proposing substitutes, qty changes, or unavailable items.' });
        return false;
      }

      const updates: Record<string, unknown> = {
        approval_reason: la.approvalReason?.trim() || null,
      };

      if (la.status === 'unavailable') {
        updates.status = 'unavailable';
        updates.approved_qty = null;
        updates.substitute_status = null;
      } else {
        if (la.approvedQty <= 0) {
          updates.status = 'unavailable';
          updates.approved_qty = null;
          updates.substitute_status = null;
        } else {
          updates.approved_qty = la.approvedQty;
          updates.status = 'approved';
          updates.substitute_status = row.substitute_status === 'accepted' ? 'accepted' : null;
        }
      }

      if (la.itemId) updates.item_id = la.itemId;
      if (la.substituteItemId) {
        updates.substitute_item_id = la.substituteItemId;
      } else if (row.substitute_item_id && row.substitute_status === 'accepted') {
        updates.substitute_item_id = row.substitute_item_id;
        updates.substitute_status = 'accepted';
      }
      if (la.resolutionNotes) updates.resolution_notes = la.resolutionNotes.trim();
      if (la.poReference !== undefined) updates.po_reference = la.poReference?.trim() || null;
      if (la.expectedDate !== undefined) updates.expected_date = la.expectedDate || null;

      const { error: lineUpdateError } = await supabase
        .from('stock_request_lines')
        .update(updates)
        .eq('id', la.lineId);
      if (lineUpdateError) {
        set({ error: `Failed to update line: ${lineUpdateError.message}` });
        return false;
      }

      const substituteId = la.substituteItemId
        ?? (row.substitute_item_id && row.substitute_status === 'accepted' ? row.substitute_item_id : null);

      if (la.status === 'unavailable') {
        await logEvent(id, 'line_unavailable', la.approvalReason?.trim() || 'Marked unavailable', la.lineId);
      } else if (substituteId && substituteId !== row.item_id) {
        const { description, payload } = substituteActivityDesc(
          'Approved with substitute',
          row.item_id,
          substituteId,
          row.free_text_description,
          la.approvedQty,
        );
        await logEvent(id, 'line_approved', description, la.lineId, payload);
      } else {
        await logEvent(id, 'line_approved', `Approved qty ${la.approvedQty}`, la.lineId);
      }
    }

    const now = new Date().toISOString();
    const { error: requestUpdateError } = await supabase
      .from('stock_requests')
      .update({
        status: 'approved',
        approved_at: now,
        reviewed_by: user.id,
      })
      .eq('id', id);
    if (requestUpdateError) {
      set({ error: `Failed to update request: ${requestUpdateError.message}` });
      return false;
    }

    await logEvent(id, 'approved', 'Request approved');

    const reqRow = await supabase
      .from('stock_requests')
      .select('requester_id, request_number')
      .eq('id', id)
      .single();
    const ref = reqRow.data ? `SR-${reqRow.data.request_number}` : 'a request';

    await useStockRequestSettingsStore.getState().fetchSettings();
    const settings = useStockRequestSettingsStore.getState().settings;

    try {
      const ordererId = pickAssignee(defaultManagerIds(settings), user.id);
      if (ordererId) {
        await assignRequest(
          id,
          ordererId,
          'order',
          'assigned',
          `${ref} has been approved and assigned to you to place the order.`,
        );
      } else {
        await supabase
          .from('stock_requests')
          .update({ assigned_to: null, assignment_step: null })
          .eq('id', id);
      }
      if (reqRow.data?.requester_id) {
        const { useStockNotificationsStore } = await import('./stockNotificationsStore');
        await useStockNotificationsStore.getState().notify(
          reqRow.data.requester_id,
          id,
          'approved',
          `Your request ${ref} has been approved.`,
        );
      }
    } catch (assignErr) {
      set({ error: assignErr instanceof Error ? assignErr.message : 'Failed to assign request' });
      return false;
    }

    await get().fetchRequests();
    await get().fetchRequestDetail(id);
    return true;
  },

  markOrdered: async (id: string, lineOrders?: LineOrderInput[]) => {
    if (!lineOrders || lineOrders.length === 0) {
      set({ error: 'Select at least one line to order.' });
      return false;
    }

    const payload = lineOrders.map(o => ({
      line_id: o.lineId,
      po_reference: o.poReference?.trim() || null,
      supplier: o.supplier?.trim() || null,
      expected_date: o.expectedDate || null,
      ordered_qty: o.orderedQty ?? null,
      ordered_at: o.orderedAt || null,
    }));

    const { data, error } = await supabase.rpc('mark_stock_request_ordered', {
      p_request_id: id,
      p_line_orders: payload,
      p_user_display_name: getActedByName() ?? 'Unknown',
    });

    if (error) {
      const missingFn = error.code === 'PGRST202'
        || /could not find the function|PGRST202/i.test(error.message);
      set({
        error: missingFn
          ? 'Database migration required: run supabase/migrations/20260903150000_line_ordered_qty_and_sheet_order_rpc.sql in the Supabase SQL editor.'
          : error.message,
      });
      return false;
    }

    const result = data as {
      success?: boolean;
      message?: string;
      status?: string;
      lines_ordered?: number;
    } | null;

    if (!result?.success) {
      set({ error: result?.message ?? 'Failed to mark as ordered.' });
      return false;
    }

    const reqRow = await supabase
      .from('stock_requests')
      .select('requester_id, request_number')
      .eq('id', id)
      .single();
    if (reqRow.data?.requester_id) {
      const { useStockNotificationsStore } = await import('./stockNotificationsStore');
      const ref = `SR-${reqRow.data.request_number}`;
      const linesOrdered = result.lines_ordered ?? lineOrders.length;
      await useStockNotificationsStore.getState().notify(
        reqRow.data.requester_id,
        id,
        'ordered',
        result.status === 'ordered'
          ? `Your request ${ref} has been marked as ordered.`
          : `${linesOrdered} line${linesOrdered === 1 ? '' : 's'} on ${ref} marked as ordered.`,
      );
    }

    await get().fetchRequests();
    await get().fetchRequestDetail(id);
    return true;
  },

  receiveLines: async (id: string, receipts: LineReceiveInput[]) => {
    if (receipts.length === 0) {
      return { success: false, message: 'Nothing to receive.' };
    }

    const { data: headerBefore } = await supabase
      .from('stock_requests')
      .select('status, job_id')
      .eq('id', id)
      .single();
    const prevStatus = (headerBefore?.status as string | undefined) ?? '';
    const linkedToJob = !!headerBefore?.job_id;

    const payload = receipts.map(r => ({
      line_id: r.lineId,
      qty: r.qty,
      skip_serial_validation: r.skipSerialValidation ?? false,
      skip_reason: r.skipReason ?? null,
      units: r.units.map(u => ({ serial: u.serial ?? '', mac: u.mac ?? '' })),
    }));

    const { data, error } = await supabase.rpc('confirm_stock_request_receipt', {
      p_request_id: id,
      p_receipts: payload,
      p_user_display_name: getActedByName() ?? 'Unknown',
    });

    if (error) {
      return { success: false, message: error.message };
    }

    const result = data as { success?: boolean; message?: string; units_added?: number } | null;
    if (!result?.success) {
      return { success: false, message: result?.message ?? 'Receipt failed.' };
    }

    const inv = useInventoryStore.getState();
    await get().fetchRequests();
    await get().fetchRequestDetail(id);
    await inv.fetchStockUnits();
    await inv.fetchRecentLogs();

    if (linkedToJob) {
      await notifyJobStockReceipt(id, prevStatus, get);
    }

    return {
      success: true,
      message: result.message ?? `${result.units_added ?? 0} unit(s) added to inventory.`,
    };
  },

  reportReceiveIssue: async (requestId, input) => {
    const reason = input.reason.trim();
    if (!reason) {
      return { success: false, message: 'Describe the receive issue.' };
    }

    const existing = get().requests.find(r => r.id === requestId);
    const line = (existing?.lines ?? []).find(l => l.id === input.lineId);
    if (!line) {
      return { success: false, message: 'Line not found' };
    }
    if (!['ordered', 'partially_received', 'received'].includes(line.status)) {
      return { success: false, message: 'This line is not in receiving.' };
    }

    const qty = input.qty && input.qty > 0 ? input.qty : 0;
    if (qty > 0) {
      const remaining = remainingReceiveQty(line);
      if (remaining <= 0) {
        return { success: false, message: 'Nothing left to add to inventory on this line. The issue can still be flagged without a qty.' };
      }
      const addQty = Math.min(qty, remaining);
      if (input.itemId && input.itemId !== effectiveLineItemId(line)) {
        const { error: subErr } = await supabase
          .from('stock_request_lines')
          .update({
            substitute_item_id: input.itemId,
            substitute_status: 'accepted',
          })
          .eq('id', line.id);
        if (subErr) return { success: false, message: subErr.message };
      }
      const received = await get().receiveLines(requestId, [{
        lineId: line.id,
        qty: addQty,
        units: input.units ?? Array.from({ length: addQty }, () => ({ serial: '', mac: '' })),
        skipSerialValidation: input.skipSerialValidation,
        skipReason: input.skipReason,
      }]);
      if (!received.success) return received;
    }

    const ok = await get().disputeLine(requestId, input.lineId, reason);
    if (!ok) {
      return { success: false, message: get().error ?? 'Failed to flag receive issue.' };
    }
    return {
      success: true,
      message: qty > 0
        ? `Issue flagged. ${qty} unit(s) added to inventory.`
        : 'Issue flagged. No stock was added.',
    };
  },

  acceptSubstitute: async (requestId: string, lineId: string) => {
    const user = useAuthStore.getState().user;
    if (!user) return false;

    const { data: line, error: fetchErr } = await supabase
      .from('stock_request_lines')
      .select('*')
      .eq('id', lineId)
      .single();
    if (fetchErr || !line) { set({ error: fetchErr?.message ?? 'Line not found' }); return false; }

    const { data: req, error: reqErr } = await supabase
      .from('stock_requests')
      .select('requester_id, request_number, reviewed_by, assigned_to')
      .eq('id', requestId)
      .single();
    if (reqErr || !req) { set({ error: reqErr?.message ?? 'Request not found' }); return false; }

    const pending = line.substitute_status as SubstituteStatus;
    if (pending !== 'pending_requester' && pending !== 'pending_manager') {
      set({ error: 'This line has no proposal awaiting a response.' });
      return false;
    }
    if (!canRespondToProposal(pending, user.id, req.requester_id, req.reviewed_by, req.assigned_to)) {
      set({ error: 'You are not assigned to respond to this proposal.' });
      return false;
    }

    const updates: Record<string, unknown> = { substitute_status: 'accepted' };
    if (line.substitute_item_id) {
      updates.status = 'submitted';
    } else {
      updates.status = 'unavailable';
      updates.approved_qty = null;
    }

    const { error: lineErr } = await supabase
      .from('stock_request_lines')
      .update(updates)
      .eq('id', lineId);
    if (lineErr) { set({ error: lineErr.message }); return false; }

    const who = responderLabel(pending);
    const { description, payload } = line.substitute_item_id
      ? substituteActivityDesc(`Substitute accepted by ${who}`, line.item_id, line.substitute_item_id, line.free_text_description)
      : { description: `Unavailable marking accepted by ${who}`, payload: {} };
    await logEvent(
      requestId,
      line.substitute_item_id ? 'substitute_accepted' : 'unavailable_accepted',
      description,
      lineId,
      payload,
    );

    await supabase.from('stock_requests').update({ status: 'submitted' }).eq('id', requestId);

    const ref = `SR-${req.request_number}`;
    try {
      await tryFinalizeOrReassign(requestId, ref);
    } catch (assignErr) {
      set({ error: assignErr instanceof Error ? assignErr.message : 'Failed to update request' });
      return false;
    }

    await get().fetchRequestDetail(requestId);
    await get().fetchRequests();
    return true;
  },

  declineSubstitute: async (requestId: string, lineId: string) => {
    const user = useAuthStore.getState().user;
    if (!user) return false;

    const { data: line, error: fetchErr } = await supabase
      .from('stock_request_lines')
      .select('*')
      .eq('id', lineId)
      .single();
    if (fetchErr || !line) { set({ error: fetchErr?.message ?? 'Line not found' }); return false; }

    const { data: req, error: reqErr } = await supabase
      .from('stock_requests')
      .select('requester_id, request_number, reviewed_by, assigned_to')
      .eq('id', requestId)
      .single();
    if (reqErr || !req) { set({ error: reqErr?.message ?? 'Request not found' }); return false; }

    const pending = line.substitute_status as SubstituteStatus;
    if (pending !== 'pending_requester' && pending !== 'pending_manager') {
      set({ error: 'This line has no proposal awaiting a response.' });
      return false;
    }
    if (!canRespondToProposal(pending, user.id, req.requester_id, req.reviewed_by, req.assigned_to)) {
      set({ error: 'You are not assigned to respond to this proposal.' });
      return false;
    }

    const updates: Record<string, unknown> = {
      substitute_status: 'declined',
      status: 'submitted',
      approval_reason: null,
      approved_qty: line.requested_qty,
      substitute_item_id: null,
    };

    const { error: lineErr } = await supabase
      .from('stock_request_lines')
      .update(updates)
      .eq('id', lineId);
    if (lineErr) { set({ error: lineErr.message }); return false; }

    const who = responderLabel(pending);
    const { description, payload } = line.substitute_item_id
      ? substituteActivityDesc(`Substitute declined by ${who}`, line.item_id, line.substitute_item_id, line.free_text_description)
      : { description: `Unavailable marking declined by ${who}`, payload: {} };
    await logEvent(
      requestId,
      line.substitute_item_id ? 'substitute_declined' : 'unavailable_declined',
      description,
      lineId,
      payload,
    );

    await supabase.from('stock_requests').update({ status: 'submitted' }).eq('id', requestId);

    const ref = `SR-${req.request_number}`;
    try {
      if (pending === 'pending_requester') {
        await reassignToManager(
          requestId,
          `${ref} — requester declined a proposed change. Re-review required.`,
          lineId,
        );
      } else if (req.requester_id) {
        await assignRequest(
          requestId,
          req.requester_id,
          'requester',
          'review',
          `${ref} — manager declined your counter-proposal.`,
          lineId,
        );
      }
    } catch (assignErr) {
      set({ error: assignErr instanceof Error ? assignErr.message : 'Failed to assign request' });
      return false;
    }

    await get().fetchRequestDetail(requestId);
    await get().fetchRequests();
    return true;
  },

  counterPropose: async (requestId: string, lineId: string, input: CounterProposalInput) => {
    const user = useAuthStore.getState().user;
    if (!user) return false;

    const { data: line, error: fetchErr } = await supabase
      .from('stock_request_lines')
      .select('*')
      .eq('id', lineId)
      .single();
    if (fetchErr || !line) { set({ error: fetchErr?.message ?? 'Line not found' }); return false; }

    const { data: req, error: reqErr } = await supabase
      .from('stock_requests')
      .select('requester_id, request_number, reviewed_by, assigned_to')
      .eq('id', requestId)
      .single();
    if (reqErr || !req) { set({ error: reqErr?.message ?? 'Request not found' }); return false; }

    const pending = line.substitute_status as SubstituteStatus;
    if (pending !== 'pending_requester' && pending !== 'pending_manager') {
      set({ error: 'This line has no proposal awaiting a response.' });
      return false;
    }
    if (!canRespondToProposal(pending, user.id, req.requester_id, req.reviewed_by, req.assigned_to)) {
      set({ error: 'You are not assigned to respond to this proposal.' });
      return false;
    }

    if (!input.unavailable && !input.substituteItemId) {
      set({ error: 'Choose a substitute item for your counter-proposal.' });
      return false;
    }

    const approvedQty = input.approvedQty ?? line.approved_qty ?? line.requested_qty;
    const nextPending: SubstituteStatus = pending === 'pending_requester'
      ? 'pending_manager'
      : 'pending_requester';

    const updates: Record<string, unknown> = {
      status: 'submitted',
      substitute_status: nextPending,
      approved_qty: input.unavailable ? null : approvedQty,
      approval_reason: input.reason?.trim() || null,
      substitute_item_id: input.unavailable ? null : input.substituteItemId,
    };

    const { error: lineErr } = await supabase
      .from('stock_request_lines')
      .update(updates)
      .eq('id', lineId);
    if (lineErr) { set({ error: lineErr.message }); return false; }

    const who = responderLabel(pending);
    const { description, payload } = input.unavailable
      ? { description: `Counter-proposal by ${who}: unavailable`, payload: {} }
      : substituteActivityDesc(`Counter-proposal by ${who}`, line.item_id, input.substituteItemId ?? null, line.free_text_description, approvedQty);
    await logEvent(requestId, 'substitute_counter_proposed', description, lineId, payload);

    await supabase.from('stock_requests').update({ status: 'submitted' }).eq('id', requestId);

    const ref = `SR-${req.request_number}`;
    try {
      if (nextPending === 'pending_manager') {
        await useStockRequestSettingsStore.getState().fetchSettings();
        const settings = useStockRequestSettingsStore.getState().settings;
        const managerId = req.reviewed_by ?? pickAssignee(defaultManagerIds(settings));
        if (managerId) {
          await assignRequest(
            requestId,
            managerId,
            'review',
            'review',
            `${ref} — counter-proposal needs your response.`,
            lineId,
          );
        }
      } else if (req.requester_id) {
        await assignRequest(
          requestId,
          req.requester_id,
          'requester',
          'review',
          `${ref} — counter-proposal needs your response.`,
          lineId,
        );
      }
    } catch (assignErr) {
      set({ error: assignErr instanceof Error ? assignErr.message : 'Failed to assign request' });
      return false;
    }

    await get().fetchRequestDetail(requestId);
    await get().fetchRequests();
    return true;
  },

  splitLine: async (requestId: string, lineId: string, splitQty: number, overrideItemId?: string | null) => {
    if (splitQty <= 0) return false;

    const req =
      get().selectedRequest?.id === requestId
        ? get().selectedRequest
        : get().requests.find(r => r.id === requestId);

    let line = req?.lines?.find(l => l.id === lineId);
    if (!line) {
      const { data: lineRow, error: lineErr } = await supabase
        .from('stock_request_lines')
        .select('*')
        .eq('id', lineId)
        .single();
      if (lineErr || !lineRow) {
        set({ error: lineErr?.message ?? 'Line not found' });
        return false;
      }
      line = toLine(lineRow as DBStockRequestLine);
    }
    const parentLine = line;

    let siblings = (req?.lines ?? []).filter(
      l => l.id !== lineId && l.sortOrder > parentLine.sortOrder,
    );
    if (siblings.length === 0) {
      const { data: siblingRows, error: siblingsErr } = await supabase
        .from('stock_request_lines')
        .select('*')
        .eq('request_id', requestId)
        .gt('sort_order', parentLine.sortOrder)
        .neq('id', lineId);
      if (siblingsErr) {
        set({ error: siblingsErr.message });
        return false;
      }
      siblings = (siblingRows ?? []).map(r => toLine(r as DBStockRequestLine));
    }
    if (siblings.length > 0) {
      await Promise.all(
        siblings.map(sib =>
          supabase.from('stock_request_lines').update({ sort_order: sib.sortOrder + 1 }).eq('id', sib.id),
        ),
      );
    }

    const { data: newLine, error } = await supabase
      .from('stock_request_lines')
      .insert({
        request_id: requestId,
        line_type: parentLine.lineType,
        item_id: overrideItemId ?? parentLine.itemId,
        free_text_description: parentLine.freeTextDescription,
        substitute_item_id: null,
        requested_qty: splitQty,
        approved_qty: splitQty,
        received_qty: 0,
        condition: parentLine.condition,
        status: 'approved',
        sort_order: parentLine.sortOrder + 1,
        parent_line_id: parentLine.id,
      })
      .select()
      .single();

    if (error || !newLine) { set({ error: error?.message ?? 'Split failed' }); return false; }

    const desc = overrideItemId && overrideItemId !== parentLine.itemId
      ? `Split: ${splitQty} unit(s) to different SKU`
      : `Split: ${splitQty} unit(s) moved to new line`;
    await logEvent(requestId, 'line_split', desc, lineId, { split_line_id: newLine.id, split_qty: splitQty });
    await get().fetchRequestDetail(requestId);
    return true;
  },

  disputeLine: async (requestId: string, lineId: string, reason: string) => {
    const { error } = await supabase
      .from('stock_request_lines')
      .update({ status: 'disputed', dispute_reason: reason.trim() || 'Item disputed' })
      .eq('id', lineId);
    if (error) { set({ error: error.message }); return false; }
    await logEvent(requestId, 'line_disputed', reason.trim() || 'Item disputed', lineId);
    await get().fetchRequestDetail(requestId);
    await get().fetchRequests();

    // Notify and reassign the reviewer
    const reqRow = await supabase.from('stock_requests').select('reviewed_by, request_number').eq('id', requestId).single();
    const ref = reqRow.data ? `SR-${reqRow.data.request_number}` : 'a request';
    const reviewerId = reqRow.data?.reviewed_by;

    if (reviewerId) {
      try {
        await assignRequest(
          requestId,
          reviewerId,
          'review',
          'disputed',
          `A line in ${ref} has been flagged as disputed.`,
          lineId,
        );
      } catch {
        const { useStockNotificationsStore } = await import('./stockNotificationsStore');
        await useStockNotificationsStore.getState().notify(
          reviewerId,
          requestId,
          'disputed',
          `A line in ${ref} has been flagged as disputed.`,
          lineId,
        );
      }
    }
    return true;
  },

  resolveDispute: async (requestId: string, lineId: string, resolution: 'accept' | 'reorder' | 'cancel') => {
    const statusMap = { accept: 'received', reorder: 'approved', cancel: 'cancelled' } as const;
    const descMap = {
      accept: 'Dispute resolved — accepted as received',
      reorder: 'Dispute resolved — marked for re-order',
      cancel: 'Dispute resolved — line cancelled',
    };
    const { error } = await supabase
      .from('stock_request_lines')
      .update({ status: statusMap[resolution], dispute_reason: null })
      .eq('id', lineId);
    if (error) { set({ error: error.message }); return false; }
    await logEvent(requestId, 'dispute_resolved', descMap[resolution], lineId);
    await get().fetchRequestDetail(requestId);
    await get().fetchRequests();
    return true;
  },

  cancelLine: async (requestId: string, lineId: string, reason: string) => {
    const { error } = await supabase
      .from('stock_request_lines')
      .update({ status: 'cancelled', resolution_notes: reason.trim() || 'Cancelled' })
      .eq('id', lineId);
    if (error) { set({ error: error.message }); return false; }
    await logEvent(requestId, 'line_cancelled', reason.trim() || 'Line cancelled', lineId);
    await get().fetchRequestDetail(requestId);
    await get().fetchRequests();
    return true;
  },

  backorderLine: async (requestId: string, lineId: string, note: string, expectedDate?: string) => {
    const updates: Record<string, unknown> = {
      status: 'backordered',
      resolution_notes: note.trim() || 'Backordered',
    };
    if (expectedDate) updates.expected_date = expectedDate;

    const { error } = await supabase
      .from('stock_request_lines')
      .update(updates)
      .eq('id', lineId);
    if (error) { set({ error: error.message }); return false; }
    const desc = expectedDate
      ? `Backordered — expected ${expectedDate}${note.trim() ? ': ' + note.trim() : ''}`
      : `Backordered${note.trim() ? ': ' + note.trim() : ''}`;
    await logEvent(requestId, 'line_backordered', desc, lineId);
    await get().fetchRequestDetail(requestId);
    await get().fetchRequests();
    return true;
  },

  cancelRequest: async (id: string) => {
    const { error } = await supabase
      .from('stock_requests')
      .update({ status: 'cancelled' })
      .eq('id', id);

    if (error) {
      set({ error: error.message });
      return false;
    }

    await supabase
      .from('stock_request_lines')
      .update({ status: 'cancelled' })
      .eq('request_id', id);

    await logEvent(id, 'cancelled', 'Request cancelled');
    await get().fetchRequests();
    await get().fetchRequestDetail(id);
    return true;
  },

  deleteRequest: async (id: string) => {
    const { data, error } = await supabase
      .from('stock_requests')
      .delete()
      .eq('id', id)
      .select('id');

    if (error) {
      set({ error: error.message });
      return false;
    }

    if (!data?.length) {
      set({ error: 'Could not delete request. You may not have permission.' });
      return false;
    }

    markOwnWrite('stock_requests', id);
    set(s => ({
      requests: s.requests.filter(r => r.id !== id),
      selectedRequest: s.selectedRequest?.id === id ? null : s.selectedRequest,
    }));
    return true;
  },

  updateRequestLinks: async (requestId, links) => {
    const updates: Record<string, unknown> = {};
    if (links.siteId !== undefined) updates.site_id = links.siteId;
    if (links.jobId !== undefined) updates.job_id = links.jobId;

    const { error } = await supabase
      .from('stock_requests')
      .update(updates)
      .eq('id', requestId);

    if (error) {
      set({ error: error.message });
      return false;
    }

    const parts: string[] = [];
    if (links.jobId !== undefined && links.siteId !== undefined && !links.jobId && !links.siteId) {
      parts.push('Site and job unlinked');
    } else {
      if (links.jobId !== undefined) {
        parts.push(links.jobId ? 'Job linked' : 'Job unlinked');
      }
      if (links.siteId !== undefined && links.jobId === undefined) {
        parts.push(links.siteId ? 'Site linked' : 'Site unlinked');
      }
    }
    await logEvent(requestId, 'links_updated', parts.join(' · ') || 'Links updated');

    set(s => ({
      requests: s.requests.map(r => {
        if (r.id !== requestId) return r;
        return {
          ...r,
          siteId: links.siteId !== undefined ? links.siteId : r.siteId,
          jobId: links.jobId !== undefined ? links.jobId : r.jobId,
          updatedAt: new Date(),
        };
      }),
      selectedRequest: s.selectedRequest?.id === requestId
        ? {
            ...s.selectedRequest,
            siteId: links.siteId !== undefined ? links.siteId : s.selectedRequest.siteId,
            jobId: links.jobId !== undefined ? links.jobId : s.selectedRequest.jobId,
            updatedAt: new Date(),
          }
        : s.selectedRequest,
    }));

    await get().fetchRequestDetail(requestId);
    await get().fetchRequests();
    return true;
  },

  fetchRequestsForJob: async (jobId: string) => {
    const collectionId = getActiveCollectionId();
    if (!collectionId) return [];

    const { data, error } = await supabase
      .from('stock_requests')
      .select('*')
      .eq('collection_id', collectionId)
      .eq('job_id', jobId)
      .order('created_at', { ascending: false });

    if (error || !data) return [];

    const requestRows = data as DBStockRequest[];
    const requestIds = requestRows.map(row => row.id);
    if (requestIds.length === 0) return [];

    const { data: linesData } = await supabase
      .from('stock_request_lines')
      .select('*')
      .in('request_id', requestIds)
      .order('sort_order', { ascending: true });

    const linesByRequest = new Map<string, StockRequestLine[]>();
    for (const row of (linesData ?? []) as DBStockRequestLine[]) {
      const line = toLine(row);
      const lines = linesByRequest.get(line.requestId) ?? [];
      lines.push(line);
      linesByRequest.set(line.requestId, lines);
    }

    return requestRows.map(row => toRequest(row, linesByRequest.get(row.id) ?? [], []));
  },

  applyRemoteRow: (table, event, row) => {
    if (!row?.id) return;
    if (isOwnWrite(table, row.id as string)) return;
    const collectionId = getActiveCollectionId();

    if (table === 'stock_requests') {
      if (row.collection_id && collectionId && row.collection_id !== collectionId) return;
      if (event === 'DELETE') {
        set((s) => ({
          requests: s.requests.filter((r) => r.id !== row.id),
          selectedRequest: s.selectedRequest?.id === row.id ? null : s.selectedRequest,
        }));
        return;
      }
      const mapped = toRequest(row as unknown as DBStockRequest);
      set((s) => {
        const existing = s.requests.find((r) => r.id === mapped.id);
        const next: StockRequest = existing
          ? { ...mapped, lines: existing.lines, events: existing.events }
          : { ...mapped, lines: mapped.lines ?? [], events: mapped.events ?? [] };
        return {
          requests: [...s.requests.filter((r) => r.id !== next.id), next],
          selectedRequest:
            s.selectedRequest?.id === next.id
              ? { ...next, lines: s.selectedRequest.lines, events: s.selectedRequest.events }
              : s.selectedRequest,
        };
      });
      return;
    }

    const lineId = row.id as string;
    const requestId = row.request_id as string | undefined;
    if (event === 'DELETE') {
      set((s) => ({
        requests: s.requests.map((r) => ({
          ...r,
          lines: (r.lines ?? []).filter((l) => l.id !== lineId),
        })),
        selectedRequest: s.selectedRequest
          ? { ...s.selectedRequest, lines: (s.selectedRequest.lines ?? []).filter((l) => l.id !== lineId) }
          : s.selectedRequest,
      }));
      return;
    }
    if (!requestId) return;
    const existingReq = get().requests.find((r) => r.id === requestId);
    if (!existingReq) {
      void get().loadRequestDetail(requestId).then((detail) => {
        if (!detail) return;
        if (collectionId && detail.collectionId !== collectionId) return;
        set((s) => ({
          requests: [...s.requests.filter((r) => r.id !== detail.id), detail],
          selectedRequest: s.selectedRequest?.id === detail.id ? detail : s.selectedRequest,
        }));
      });
      return;
    }
    const line = toLine(row as unknown as DBStockRequestLine);
    set((s) => {
      const patchRequest = (r: StockRequest): StockRequest =>
        r.id !== requestId
          ? r
          : { ...r, lines: [...(r.lines ?? []).filter((l) => l.id !== line.id), line] };
      return {
        requests: s.requests.map(patchRequest),
        selectedRequest: s.selectedRequest ? patchRequest(s.selectedRequest) : s.selectedRequest,
      };
    });
  },
}));

/** Display label for a request */
export function formatRequestRef(requestNumber: number): string {
  return `SR-${requestNumber}`;
}

/** Received vs approved/requested qty across active lines */
export function requestReceiveProgress(req: StockRequest): { received: number; total: number } {
  const lines = (req.lines ?? []).filter(l => l.status !== 'cancelled');
  const total = lines.reduce((sum, l) => sum + (l.approvedQty ?? l.requestedQty), 0);
  const received = lines.reduce((sum, l) => sum + l.receivedQty, 0);
  return { received, total };
}

/** dd/mm/yyyy hh:mm — matches Inventory */
export function fmtRequestDate(d: Date | null | undefined): string {
  if (!d) return '—';
  const date = d instanceof Date ? d : new Date(d);
  if (isNaN(date.getTime())) return '—';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes}`;
}

/** dd/mm/yyyy for date-only values (e.g. expected delivery). Avoids timezone shifts on yyyy-mm-dd strings. */
export function fmtRequestDay(d: string | Date | null | undefined): string {
  if (!d) return '—';
  if (typeof d === 'string') {
    const iso = d.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
    const dmy = d.trim().match(/^(\d{2})\/(\d{2})\/(\d{4})/);
    if (dmy) return `${dmy[1]}/${dmy[2]}/${dmy[3]}`;
  }
  const date = d instanceof Date ? d : new Date(d);
  if (isNaN(date.getTime())) return '—';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

/** Parse dd/mm/yyyy (or yyyy-mm-dd) into yyyy-mm-dd, or null if invalid/empty. */
export function parseRequestDay(input: string): string | null {
  const trimmed = input.trim();
  if (!trimmed) return null;

  const dmy = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (dmy) {
    const day = Number(dmy[1]);
    const month = Number(dmy[2]);
    const year = Number(dmy[3]);
    if (month < 1 || month > 12 || day < 1 || day > 31) return null;
    const probe = new Date(year, month - 1, day);
    if (probe.getFullYear() !== year || probe.getMonth() !== month - 1 || probe.getDate() !== day) return null;
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }

  const iso = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) {
    const year = Number(iso[1]);
    const month = Number(iso[2]);
    const day = Number(iso[3]);
    const probe = new Date(year, month - 1, day);
    if (probe.getFullYear() !== year || probe.getMonth() !== month - 1 || probe.getDate() !== day) return null;
    return `${iso[1]}-${iso[2]}-${iso[3]}`;
  }

  return null;
}

export function lineNeedsResolution(line: StockRequestLine): boolean {
  return line.lineType === 'free_text' && !line.itemId && line.status !== 'cancelled' && line.status !== 'unavailable';
}

export function effectiveLineItemId(line: StockRequestLine): string | null {
  return line.substituteItemId ?? line.itemId;
}

export function remainingReceiveQty(line: StockRequestLine): number {
  const target = line.orderedQty ?? line.approvedQty ?? line.requestedQty;
  return Math.max(0, target - line.receivedQty);
}

export function isLineApprovable(line: StockRequestLine): boolean {
  return line.status === 'draft' || line.status === 'submitted' || line.status === 'pending_resolution';
}

export function isLineOrderable(line: StockRequestLine): boolean {
  return line.status === 'approved';
}

/** Line is ordered and still has quantity that can be added to inventory. */
export function isLineReceivable(line: StockRequestLine): boolean {
  return (line.status === 'ordered' || line.status === 'partially_received')
    && remainingReceiveQty(line) > 0
    && !!effectiveLineItemId(line);
}

export function isLineIssueable(line: StockRequestLine): boolean {
  return line.status === 'ordered'
    || line.status === 'partially_received'
    || line.status === 'received';
}

/** Statuses that only the receive flow should set. */
export function isReceiptDerivedStatus(status: LineStatus): boolean {
  return status === 'received' || status === 'partially_received';
}
